import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, X } from "lucide-react";
import { BIZ, REVIEWS } from "../data/site";

const HIDE_ON = ["/get-quote", "/contact", "/review"];
const POPUP_REVIEWS = [...REVIEWS].sort((a, b) => b.text.length - a.text.length).slice(0, 4);

export const ReviewPopup = () => {
  const { pathname } = useLocation();
  const [closed, setClosed] = useState(false);
  const [i, setI] = useState(0);
  const [show, setShow] = useState(false);
  const [cycle, setCycle] = useState(0);
  const [first, setFirst] = useState(true);
  const [ready, setReady] = useState(() => window.innerWidth >= 768);
  const hidden = closed || !ready || HIDE_ON.includes(pathname);

  // On phones, wait until the visitor scrolls past the hero so the card never covers the headline or buttons
  useEffect(() => {
    if (ready) return undefined;
    const onScroll = () => { if (window.scrollY > 600) setReady(true); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [ready]);

  useEffect(() => { setShow(false); setCycle((c) => c + 1); }, [pathname]);

  useEffect(() => {
    if (hidden) return undefined;
    const t = show
      ? setTimeout(() => { setShow(false); setFirst(false); setI((n) => (n + 1) % POPUP_REVIEWS.length); }, 9000)
      : setTimeout(() => setShow(true), first ? (window.innerWidth >= 768 ? 4000 : 1200) : 2500);
    return () => clearTimeout(t);
  }, [show, hidden, cycle, first]);

  const r = POPUP_REVIEWS[i];
  return (
    <AnimatePresence>
      {!hidden && show && r && (
        <motion.div key={r.name} initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-3 z-[45] w-[calc(100%-1.5rem)] max-w-[290px] rounded-xl border border-slate-200 bg-white p-3 shadow-xl sm:left-5 md:bottom-6" role="status" data-testid="review-popup">
          <button onClick={() => setClosed(true)} className="absolute right-1.5 top-1.5 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-900" aria-label="Close review" data-testid="review-popup-close"><X className="h-3.5 w-3.5" /></button>
          <div className="flex items-center gap-1.5">
            <div className="flex">{[0, 1, 2, 3, 4].map((s) => <Star key={s} className="h-3 w-3 fill-[#fbbc04] text-[#fbbc04]" />)}</div>
            <span className="text-xs font-semibold text-slate-500">Verified review</span>
          </div>
          <p className="mt-1.5 line-clamp-2 pr-4 text-xs leading-snug text-slate-800 md:line-clamp-3" data-testid="review-popup-text">“{r.text}”</p>
          <div className="mt-2 flex items-center justify-between gap-2 text-xs">
            <span className="font-bold text-slate-900">{r.name} <span className="font-normal text-slate-500">· {r.tag}</span></span>
            <a href={BIZ.google} target="_blank" rel="noreferrer" className="shrink-0 font-semibold text-slate-900 underline underline-offset-2" data-testid="review-popup-google-link">5.0★ on Google</a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
